import { Box, Card, CircularProgress } from '@mui/material'
import { IWorkFlow } from '../../definitions/Workflow'
import { useEffect, useState } from 'react'
import EditorForm from '../editor/form_builder/EditorForm'
import ApiClient from '../../backend/ApiClient'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'
import WorkflowManager from '../../managers/WorkflowManager'
import useInvalidate from '../hooks/useInvalidate'
import API_URLS from '../../backend/ApiUrls'
import { ISystemConfig } from '../../definitions/MiscSchema'
import affirm from '../../core/Affirm'
import Helper from '../../helpers/Helper'
import UsersManager from '../../managers/UsersManager'

interface P {
    workflow?: IWorkFlow
    onClose: () => void
}

export default function WorkflowEditor({ workflow: originalWorkflow, onClose }: P) {
    const [workflow, _setWorkflow] = useState<IWorkFlow>(originalWorkflow ?? WorkflowManager.createNewWorkflow())
    const [isLoading, setIsLoading] = useState(false)
    const invalidate = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllWorkflows + '/*') })
    const isNewWorkflow = !Helper.hasVal(originalWorkflow)

    const { status: usersStatus, response: allUsers } = useQuery({
        queryKey: buildQueryKey('all-users'),
        queryFn: async () => await UsersManager.getAllUsers()
    })

    const { status: workflowEditorOptionsStatus, response: workflowEditorOptions } = useQuery({
        queryKey: buildQueryKey('workflow-editor-options'),
        queryFn: async () => await ApiClient.getWorkflowEditorOptions(),
        cache: true
    })

    useEffect(() => {
        if (!isNewWorkflow) return

        const currentUser = UsersManager.getCurrentUser()
        if (currentUser) setWorkflow({ ...workflow, createdBy: currentUser })
    }, [])

    const setWorkflow = (newW: IWorkFlow) => {
        const prevW = workflow
        for (let i = 0; i < newW.tasks.length; i++) {
            const task = newW.tasks[i]

            if (!prevW.tasks.some(x => x.uuid === task.uuid)) {
                task.assignee = newW.assignee
                task.status = 'open'
                task.startedAt = workflow.createdAt
                task.completedAt = ''
            }
        }
        _setWorkflow(newW)
    }

    const saveWorkflow = async (data: IWorkFlow) => {
        affirm(data, 'Invalid workflow')

        setIsLoading(true)
        const invalidTask = data.tasks.find(task => task.dependencies?.some(dep => dep.uuid === task.uuid))
        if (invalidTask) {
            snackbar.show({
                severity: 'error',
                text: `Task "${invalidTask.title}" cannot depend on itself`
            })
            setIsLoading(false)
            return
        }

        try {
            if (isNewWorkflow) await ApiClient.createWorkflow(data)
            else await ApiClient.updateWorkflow(data)

            invalidate()
            snackbar.show({ severity: 'success', text: 'workflow saved successfully!' })
        } catch (error) {
            console.error('Error saving workflow:', error)
            snackbar.show({ severity: 'error', text: 'Failed to save workflow' })
        } finally {
            setIsLoading(false)
        }
    }

    const marginModalVertical = 20
    const steps = WorkflowManager.getWorkflowSteps()
    const localLoading = isLoading || usersStatus === 'loading' || workflowEditorOptionsStatus === 'loading'
    const { workflowFrequency, contractsFilter } = workflowEditorOptions ?? {}
    const allTasks: ISystemConfig[] = workflow.tasks.map(task => ({
        uuid: task.uuid,
        name: task.title
    }))

    return (
        <Box
            sx={{
                margin: `${marginModalVertical}px auto ${marginModalVertical}px`,
                maxWidth: 780,
                height: '-webkit-fill-available',
                overflowY: 'auto',
                position: 'relative',
                pointerEvents: localLoading ? 'none' : undefined
            }}>
            {localLoading && (
                <Box
                    display='flex'
                    flex={1}
                    justifyContent='center'
                    alignItems='center'
                    height='var(--content-page-height)'
                    zIndex={999}
                    position='absolute'
                    bottom='calc(50% - 25px)'
                    left='calc(50% - 25px)'>
                    <CircularProgress />
                </Box>
            )}
            <Box maxWidth={780} height='-webkit-fill-available'>
                <Card sx={{ height: '100%', overflowY: 'auto' }}>
                    <Box sx={{ opacity: localLoading ? 0.5 : 1 }}>
                        <EditorForm
                            data={workflow}
                            schema={steps}
                            setData={workflow => setWorkflow(workflow)}
                            onSave={(data: IWorkFlow) => saveWorkflow(data)}
                            onComplete={onClose}
                            // @ts-ignore This is to avoid the error of a cyclical data structure comment.reply.reply...
                            scc={{
                                'assignee': allUsers,
                                'contract': contractsFilter,
                                'tasks.assignee': allUsers,
                                'tasks.dependencies': allTasks,
                                'repeats': workflowFrequency
                            }}
                            component='workflow_editor'
                        />
                    </Box>
                </Card>
            </Box>
        </Box>
    )
}
